import {
  ActivityIndicator, Keyboard, KeyboardAvoidingView, Platform, StyleSheet, Text, TextInput, View,
} from 'react-native';
import React, { useEffect, useState } from 'react';
import { FontAwesome } from '@expo/vector-icons';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../../../constants/Colors';
import updateTicketMessage from '../../../requests/Tickets/UpdateTicketMessage';
import isMobile from '../../../components/IsMobile';
import Style from '../../../constants/Style';
import PressableStyled from '../../../components/PressableStyled';
import DeleteIcon24 from '../../../components/24pxIcons/DeleteIcon24';
import Button from '../../../components/Button';
import { logEvent } from '../../../components/LogEvent';

const MAX_LENGTH: number = 300;
interface Props {
  id: string;
  updateSelectedTicketInList: Function,
  messageModalShown: boolean,
  hide: () => void,
}

const SendMessageModal = ({
  id, updateSelectedTicketInList, messageModalShown, hide,
}: Props) => {
  const [noteValue, setNoteValue] = useState<string>('');
  const [noteSaved, setNoteSaved] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const { bottom } = useSafeAreaInsets();

  useEffect(() => {
    setNoteValue('');
    setNoteSaved(false);
  }, [id]);

  return <Modal
    isVisible={messageModalShown}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    style={[Style.modal, styles.modal]}
  >
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={20}
      testID='send-message-modal'
    >
      <View style={[
        Style.modalContent, styles.container,
        { paddingBottom: Math.max(bottom, styles.container.padding) },
      ]}>
        <Text style={[Style.title, styles.title]}>{'Send message to reporter'}</Text>
        <PressableStyled
          style={Style.closeButtonBlack}
          onPress={hide}
          testID='close-send-message-modal-btn'
        >
          <DeleteIcon24 color={Colors.light.hereworksBlack600} />
        </PressableStyled>
        <TextInput
          placeholder='Type your message here...'
          placeholderTextColor={Colors.light.hereworksPrimaryGrey700}
          value={noteValue ?? ''}
          onChangeText={(text) => {
            if (!loading) setNoteValue(text);
          }}
          multiline
          style={[styles.input, focused && { borderColor: Colors.light.hereworksBlue300 }]}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            Keyboard.dismiss();
          }}
          maxLength={MAX_LENGTH}
        />
        {noteSaved && <View style={styles.messageSentTitle}>
          <Text style={styles.noteSaveStatus}>{'Message sent to the reporter'}</Text>
          <FontAwesome
            size={20}
            style={styles.icon}
            name={'check'}
          />
        </View>}
        <Button
          affirmative
          onPress={() => {
            logEvent('SendMessageModal', 'send_button');
            setLoading(true);
            updateTicketMessage(id, noteValue).then((res) => {
              if (res && !res.isError) {
                updateSelectedTicketInList(res);
                setNoteSaved(true);
                setNoteValue('');
              }
              setLoading(false);
            });
          }}
          disabled={!(noteValue?.trim().length > 0) || loading}
          style={styles.button}
          children={loading && <ActivityIndicator style={styles.loadingIndicator} />}
          title={'Send'}
          testID='send-message-modal-button'
        />
      </View>
    </KeyboardAvoidingView>
  </Modal>;
};

const styles = StyleSheet.create({
  modal: isMobile ? {
    justifyContent: 'flex-end',
  } : {},
  container: {
    width: isMobile ? '100%' : 540,
    alignSelf: 'center',
    padding: isMobile ? 20 : 24,
    borderBottomStartRadius: isMobile ? 0 : undefined,
    borderBottomEndRadius: isMobile ? 0 : undefined,
  },
  title: {
    marginBottom: 12,
  },
  noteSaveStatus: {
    alignSelf: 'flex-end',
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    color: Colors.light.hereworksPrimaryGrey700,
  },
  input: {
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    height: 100,
    backgroundColor: Colors.light.hereworksBackground,
    marginTop: 10,
    marginBottom: 4,
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 12,
    verticalAlign: 'top',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    outlineWidth: 0,
  },
  messageSentTitle: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 8,
  },
  button: {
    marginTop: 12,
    marginStart: 0,
    flex: undefined,
    alignSelf: isMobile ? 'stretch' : 'center',
  },
  icon: {
    width: 20,
    marginStart: 10,
    color: Colors.light.hereworksPrimaryGrey800,
  },
  loadingIndicator: {
    marginStart: 12,
  },
});

export default SendMessageModal;
